import React, { useEffect, useState } from 'react';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import TemplateList from '../../../components/WhatsApp/TemplateList';
import UpdateBusinessModal from '../../../components/WhatsApp/UpdateBusinessModal';
import { getRequest, postRequest } from '../../../helpers/apiHandler';
import useFBAuthenticated from '../../../hooks/useFBAuthenicated';
import { showAlert } from '../../../store/actions/alertAction';
import InfoItem from '../../../components/WhatsApp/InfoItem';

const WhatsAppNumberDetails = (props) => {
  const { waba_id, phone_number_id } = useParams();
  const { access_token } = useSelector((state) => state.auth.user);
  const isFBAuthenticated = useFBAuthenticated(access_token);
  const [phoneDetails, setPhoneDetails] = useState({});
  const [showBusinessUpdateModal, setShowBusinessUpdateModal] = useState(false);
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);

  const [businessProfileData, setBusinessProfileData] = useState({
    messaging_product: 'whatsapp',
    address: '',
    description: '',
    vertical: '',
    about: '',
    email: '',
    websites: [],
  });

  // Get business profile data from phoneDetails
  const getBusinessProfileData = () => {
    const data = {
      headers: { Authorization: access_token.token },
      params: { phone_number_id: phone_number_id },
    };

    getRequest(`/v2/whatsapp/get-business-profile`, data, (res) => {
      if (res.success) {
        setBusinessProfileData(res.data.profile);
      }
    });
  };

  const handleSubmit = async (formData) => {
    try {
      const payloadData = {
        messaging_product: formData.messaging_product,
        address: formData.address,
        description: formData.description,
        vertical: formData.vertical,
        about: formData.about,
        email: formData.email,
        websites: Array.isArray(formData.websites[0])
          ? formData.websites[0]
          : [formData.websites].filter(Boolean),
        phone_number_id,
      };
      postRequest(
        '/v2/whatsapp/update-business-profile',
        payloadData,
        access_token.token,
        (res) => {
          if (res.success) {
            // Refresh phone details after successful update
            fetchPhoneDetails();
            setShowBusinessUpdateModal(false);
            dispatch(showAlert('Business profile has been updated!', 'success'));
          } else {
            console.error('Error updating business profile');
            dispatch(
              showAlert('Business profile failed to update. Please try again later.', 'danger')
            );
          }
        }
      );
    } catch (error) {
      console.error('Error updating business profile:', error);
      dispatch(
        showAlert(
          'Something went wrong while updating business profile. Please try again later.',
          'danger'
        )
      );
    }
  };

  const fetchPhoneDetails = () => {
    postRequest(
      '/v2/whatsapp/get-phone-details',
      { phone_number_id },
      access_token.token,
      (res) => {
        if (res.success) {
          const { data } = res.data;
          setPhoneDetails(data);
        } else {
          dispatch(showAlert('Failed to fetch phone details!', 'danger'));
          console.log('Error fetching phone details');
        }
      }
    );
  };

  const generateApiKey = () => {
    postRequest('/v2/whatsapp/generate-api-key', {}, access_token.token, (res) => {
      if (res.success) {
        fetchPhoneDetails();
      } else {
        dispatch(showAlert('Failed to generate API Key!', 'danger'));
        console.log('Error generating api token');
      }
    });
  };

  const deleteApiKey = () => {
    postRequest('/v2/whatsapp/delete-api-key', {}, access_token.token, (res) => {
      if (res.success) {
        fetchPhoneDetails();
      } else {
        dispatch(showAlert('Failed to delete API Key!', 'danger'));
        console.log('Error generating api token');
      }
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(phoneDetails.api_key)
      .then(() => {
        dispatch(showAlert('API Key copied to clipboard!', 'success'));
      })
      .catch((err) => {
        dispatch(showAlert('Failed to copy API Key to clipboard!', 'danger'));
        console.error('Failed to copy: ', err);
      });
  };

  useEffect(() => {
    if (isFBAuthenticated && !isLoaded) {
      fetchPhoneDetails();
      getBusinessProfileData();
      setIsLoaded(true);
    }
  }, [isFBAuthenticated, isLoaded, setIsLoaded]);

  return (
    <div className="whatsapp-page number-details">
      <UpdateBusinessModal
        show={showBusinessUpdateModal}
        onHide={() => setShowBusinessUpdateModal(false)}
        initialData={businessProfileData}
        onSubmit={handleSubmit}
      />
      <div className="page-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Link to="/dashboard/whatsapp" className="btn btn-outline-secondary btn-sm me-3">
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Dashboard
            </Link>
          </div>
        </div>
        <Row className="mb-4">
          <Col md={6}>
            <Card className="shadow-sm h-100">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0 fw-bold">Basic Info</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowBusinessUpdateModal(true)}
                    className="px-4"
                  >
                    Update
                  </Button>
                </div>
                {phoneDetails && (
                  <div className="info-grid">
                    <InfoItem label="Phone Number ID" value={phoneDetails.id} copyable />
                    <InfoItem label="Verified Name" value={phoneDetails.verified_name} />
                    <InfoItem
                      label="Code Verification Status"
                      value={phoneDetails.code_verification_status}
                      badge
                      variant={
                        phoneDetails.code_verification_status === 'VERIFIED' ? 'success' : 'warning'
                      }
                    />
                    <InfoItem
                      label="Display Phone Number"
                      value={phoneDetails.display_phone_number}
                    />
                    <InfoItem
                      label="Quality Rating"
                      value={phoneDetails.quality_rating}
                      badge
                      variant={
                        phoneDetails.quality_rating === 'HIGH'
                          ? 'success'
                          : phoneDetails.quality_rating === 'MEDIUM'
                          ? 'warning'
                          : 'danger'
                      }
                    />
                    <InfoItem label="Platform Type" value={phoneDetails.platform_type} />
                    <InfoItem
                      label="Throughput Level"
                      value={phoneDetails.throughput?.level}
                      badge
                      variant={
                        phoneDetails.throughput?.level === 'HIGH'
                          ? 'success'
                          : phoneDetails.throughput?.level === 'MEDIUM'
                          ? 'warning'
                          : 'danger'
                      }
                    />
                    <InfoItem
                      label="Last Onboarded"
                      value={new Date(phoneDetails.last_onboarded_time).toLocaleDateString()}
                    />
                    <InfoItem
                      label="Webhook URL"
                      value={phoneDetails.webhook_configuration?.application}
                      copyable
                    />
                    <div className="api-keys-section mt-4 pt-3 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-bold">API Keys</h6>
                        {!phoneDetails.api_key && (
                          <Button variant="outline-primary" size="sm" onClick={generateApiKey}>
                            Generate API Keys
                          </Button>
                        )}
                      </div>
                      {phoneDetails.api_key && (
                        <div className="api-key-container p-3 bg-light rounded">
                          <div className="d-flex align-items-center mb-2">
                            <code className="me-3 flex-grow-1">{phoneDetails.api_key}</code>
                            <div className="btn-group">
                              <Button variant="outline-primary" size="sm" onClick={copyToClipboard}>
                                <i className="fas fa-copy"></i>
                              </Button>
                              <Button variant="outline-info" size="sm" onClick={generateApiKey}>
                                <i className="fas fa-sync-alt"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={deleteApiKey}>
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <TemplateList phoneNumberId={phone_number_id} wabaId={waba_id} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default WhatsAppNumberDetails;
